import {useId, type ReactNode} from 'react';
import {useTranslation} from 'react-i18next';
import {clsx} from 'clsx';
import {useDispatch, useSelector} from '@utils';
import {Theme} from 'ghostban';
import {globalBoardSettingsSlice} from '@reducers';
import {ResponseSpeed} from '@types';
import {Board} from './Board';

type BoardSettingsFormProps = {
    showBoardSize?: boolean;
    showResponseSpeed?: boolean;
    showMoveNumbers?: boolean;
    showClearBoard?: boolean;
    onClearBoard?: () => void;
    showPreview?: boolean;
    settings?: {
        theme: Theme;
        coordinate: boolean;
        moveSound: boolean;
        moveSoundVolume: number;
        responseSpeed: ResponseSpeed;
        boardSize?: number;
        number?: boolean;
    };
    onChangeTheme?: (theme: Theme) => void;
    onChangeCoordinate?: (coordinate: boolean) => void;
    onChangeNumber?: (number: boolean) => void;
    onChangeMoveSound?: (moveSound: boolean) => void;
    onChangeSoundVolume?: (volume: number) => void;
    onChangeResponseSpeed?: (responseSpeed: ResponseSpeed) => void;
    onChangeBoardSize?: (boardSize: number | undefined) => void;
};

type ChoiceValue = string | number | boolean;

type SegmentedOption<T extends ChoiceValue> = {
    label: ReactNode;
    value: T;
};

type SegmentedControlProps<T extends ChoiceValue> = {
    ariaLabel: string;
    name: string;
    onChange: (value: T) => void;
    options: readonly SegmentedOption<T>[];
    value: T;
};

type SettingRowProps = {
    children: ReactNode;
    label: ReactNode;
    labelFor?: string;
};

type ThemeOption = {
    description?: ReactNode;
    label: string;
    value: Theme;
};

const sampleBoardMat = [
    [0, 1, 0, 0],
    [1, 0, 1, 0],
    [0, 1, -1, 0],
    [0, 0, 0, -1],
];

const SegmentedControl = <T extends ChoiceValue>({
    ariaLabel,
    name,
    onChange,
    options,
    value,
}: SegmentedControlProps<T>) => (
    <div
        aria-label={ariaLabel}
        className="inline-flex max-w-full flex-wrap gap-0.5 rounded-md bg-neutral-100 p-0.5 ring-1 ring-inset ring-neutral-200 dark:bg-neutral-800 dark:ring-neutral-700"
        role="radiogroup">
        {options.map(option => (
            <label className="relative cursor-pointer" key={String(option.value)}>
                <input
                    checked={Object.is(value, option.value)}
                    className="peer sr-only"
                    name={name}
                    onChange={() => onChange(option.value)}
                    type="radio"
                    value={String(option.value)}
                />
                <span className="inline-flex min-h-8 min-w-14 select-none items-center justify-center rounded px-2.5 py-1 text-sm font-medium text-neutral-600 transition-colors hover:bg-white/70 hover:text-neutral-950 peer-checked:bg-blue-600 peer-checked:text-white peer-checked:shadow-sm peer-focus-visible:outline-none peer-focus-visible:ring-2 peer-focus-visible:ring-blue-500 peer-focus-visible:ring-offset-1 peer-focus-visible:ring-offset-neutral-100 dark:text-neutral-300 dark:hover:bg-neutral-700 dark:hover:text-white dark:peer-checked:bg-blue-500 dark:peer-checked:text-white dark:peer-focus-visible:ring-offset-neutral-800">
                    {option.label}
                </span>
            </label>
        ))}
    </div>
);

const SettingRow = ({children, label, labelFor}: SettingRowProps) => (
    <div className="grid min-w-0 gap-2.5 py-4 sm:grid-cols-[9rem_minmax(0,1fr)] sm:items-center">
        {labelFor ? (
            <label
                className="text-sm font-semibold text-[var(--ui-text)]"
                htmlFor={labelFor}>
                {label}
            </label>
        ) : (
            <div className="text-sm font-semibold text-[var(--ui-text)]">
                {label}
            </div>
        )}
        <div className="min-w-0">{children}</div>
    </div>
);

export const BoardSettingsForm = ({
    showBoardSize = false,
    showResponseSpeed = true,
    showMoveNumbers = true,
    showClearBoard = false,
    onClearBoard,
    showPreview = true,
    settings: customSettings,
    onChangeTheme: customOnChangeTheme,
    onChangeCoordinate: customOnChangeCoordinate,
    onChangeNumber: customOnChangeNumber,
    onChangeMoveSound: customOnChangeMoveSound,
    onChangeSoundVolume: customOnChangeSoundVolume,
    onChangeResponseSpeed: customOnChangeResponseSpeed,
    onChangeBoardSize: customOnChangeBoardSize,
}: BoardSettingsFormProps) => {
    const {t} = useTranslation();
    const globalBoardSetting = useSelector(s => s.globalBoardSettings);
    const dispatch = useDispatch();
    const controlId = useId();

    const settings = customSettings ?? globalBoardSetting;
    const theme = settings.theme ?? Theme.Flat;
    const coordinate = Boolean(settings.coordinate);
    const moveNumbers = Boolean(settings.number);
    const moveSound = Boolean(settings.moveSound);
    const soundVolume = Math.round((settings.moveSoundVolume ?? 0) * 100);
    const responseSpeed = settings.responseSpeed ?? ResponseSpeed.FAST;

    const handleChangeTheme = (newTheme: Theme) => {
        if (customOnChangeTheme) {
            customOnChangeTheme(newTheme);
        } else {
            dispatch(
                globalBoardSettingsSlice.actions.setGlobalBoardSettings({
                    ...globalBoardSetting,
                    theme: newTheme,
                })
            );
        }
    };

    const handleChangeCoordinate = (newCoordinate: boolean) => {
        if (customOnChangeCoordinate) {
            customOnChangeCoordinate(newCoordinate);
        } else {
            dispatch(
                globalBoardSettingsSlice.actions.setGlobalBoardSettings({
                    ...globalBoardSetting,
                    coordinate: newCoordinate,
                })
            );
        }
    };

    const handleChangeNumber = (number: boolean) => {
        if (customOnChangeNumber) {
            customOnChangeNumber(number);
        } else {
            dispatch(
                globalBoardSettingsSlice.actions.setGlobalBoardSettings({
                    ...globalBoardSetting,
                    number,
                })
            );
        }
    };

    const handleChangeBoardSize = (boardSize: number | undefined) => {
        if (customOnChangeBoardSize) {
            customOnChangeBoardSize(boardSize);
        } else {
            dispatch(
                globalBoardSettingsSlice.actions.setGlobalBoardSettings({
                    ...globalBoardSetting,
                    boardSize,
                })
            );
        }
    };

    const handleChangeMoveSound = (newMoveSound: boolean) => {
        if (customOnChangeMoveSound) {
            customOnChangeMoveSound(newMoveSound);
        } else {
            dispatch(
                globalBoardSettingsSlice.actions.setGlobalBoardSettings({
                    moveSound: newMoveSound,
                })
            );
        }
    };

    const handleChangeSoundVolume = (value: number) => {
        if (customOnChangeSoundVolume) {
            customOnChangeSoundVolume(value / 100);
        } else {
            dispatch(
                globalBoardSettingsSlice.actions.setGlobalBoardSettings({
                    moveSoundVolume: value / 100,
                })
            );
        }
    };

    const handleChangeResponseSpeed = (newResponseSpeed: ResponseSpeed) => {
        if (customOnChangeResponseSpeed) {
            customOnChangeResponseSpeed(newResponseSpeed);
        } else {
            dispatch(
                globalBoardSettingsSlice.actions.setGlobalBoardSettings({
                    responseSpeed: newResponseSpeed,
                })
            );
        }
    };

    const themeOptions: readonly ThemeOption[] = [
        {value: Theme.Flat, label: t('board_themes.flat')},
        {value: Theme.Warm, label: t('board_themes.warm')},
        {value: Theme.Dark, label: t('board_themes.dark')},
        {value: Theme.Subdued, label: t('board_themes.subdued')},
        {
            value: Theme.Photorealistic,
            label: t('board_themes.photorealistic'),
        },
        {value: Theme.Walnut, label: t('board_themes.walnut')},
        {value: Theme.ShellStone, label: t('board_themes.shell')},
        {
            value: Theme.SlateAndShell,
            label: t('board_themes.slate_and_shell'),
        },
        {
            value: Theme.BlackAndWhite,
            label: t('board_themes.black_and_white'),
        },
        {
            value: Theme.YunziMonkeyDark,
            label: t('board_themes.yunzi_monkey_dark'),
            description: (
                <a
                    className="text-xs text-neutral-500 hover:text-blue-600 hover:underline dark:text-neutral-400"
                    href="https://patreon.com/goskins"
                    onClick={event => event.stopPropagation()}
                    rel="noopener noreferrer"
                    target="_blank">
                    {t('board_themes.more_skins_by_pumu')}
                </a>
            ),
        },
        {
            value: Theme.HighContrast,
            label: t('board_themes.high_contrast'),
        },
    ];

    const booleanOptions: readonly SegmentedOption<boolean>[] = [
        {value: true, label: t('rush_leaderboard.options.on')},
        {value: false, label: t('rush_leaderboard.options.off')},
    ];
    const responseSpeedOptions: readonly SegmentedOption<ResponseSpeed>[] = [
        {
            value: ResponseSpeed.SLOW,
            label: t('rush_settings.response_speed_options.slow'),
        },
        {
            value: ResponseSpeed.NORMAL,
            label: t('rush_settings.response_speed_options.normal'),
        },
        {
            value: ResponseSpeed.FAST,
            label: t('rush_settings.response_speed_options.fast'),
        },
        {
            value: ResponseSpeed.INSTANT,
            label: t('rush_settings.response_speed_options.instant'),
        },
    ];
    const selectedThemeLabel =
        themeOptions.find(option => option.value === theme)?.label ??
        t('board_themes.flat');
    const boardSizeId = `${controlId}-board-size`;
    const soundVolumeId = `${controlId}-sound-volume`;

    return (
        <div className="w-full max-w-5xl">
            <fieldset className="min-w-0">
                <legend className="text-sm font-semibold text-[var(--ui-text)]">
                    {t('board_themes.board_themes')}
                </legend>
                <div
                    className={clsx(
                        'mt-2.5 grid min-w-0 gap-4',
                        showPreview &&
                            'md:grid-cols-[minmax(0,1fr)_11rem] md:items-start'
                    )}>
                    <div className="grid min-w-0 grid-cols-1 gap-1.5 sm:grid-cols-2">
                        {themeOptions.map(option => (
                            <label
                                className="flex min-h-10 cursor-pointer items-center gap-2 rounded-md border border-neutral-200 bg-white px-2.5 py-2 transition-colors hover:border-neutral-300 hover:bg-neutral-50 has-[:checked]:border-blue-500 has-[:checked]:bg-blue-50 dark:border-neutral-700 dark:bg-neutral-900 dark:hover:border-neutral-600 dark:hover:bg-neutral-800 dark:has-[:checked]:border-blue-500 dark:has-[:checked]:bg-blue-950/40"
                                key={option.value}>
                                <input
                                    checked={theme === option.value}
                                    className="h-3.5 w-3.5 shrink-0 accent-blue-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-1"
                                    name={`${controlId}-theme`}
                                    onChange={() =>
                                        handleChangeTheme(option.value)
                                    }
                                    type="radio"
                                    value={option.value}
                                />
                                <span className="min-w-0 text-sm leading-4 text-[var(--ui-text)]">
                                    <span className="block font-medium">
                                        {option.label}
                                    </span>
                                    {option.description && (
                                        <span className="block">
                                            {option.description}
                                        </span>
                                    )}
                                </span>
                            </label>
                        ))}
                    </div>

                    {showPreview && (
                        <div className="mx-auto w-fit rounded-md border border-neutral-200 bg-neutral-50 p-2 shadow-sm dark:border-neutral-700 dark:bg-neutral-900 md:mx-0">
                            <div className="h-[150px] w-[150px] overflow-hidden rounded-sm">
                                <Board
                                    boardOptions={{theme, coordinate}}
                                    disabled
                                    mat={sampleBoardMat}
                                    size="150px"
                                />
                            </div>
                            <div className="mt-1.5 max-w-[150px] truncate text-center text-xs font-medium text-neutral-500 dark:text-neutral-400">
                                {selectedThemeLabel}
                            </div>
                        </div>
                    )}
                </div>
            </fieldset>

            <div className="mt-5 divide-y divide-neutral-200 border-y border-neutral-200 dark:divide-neutral-700 dark:border-neutral-700">
                <SettingRow label={t('rush_settings.coordinate')}>
                    <SegmentedControl
                        ariaLabel={t('rush_settings.coordinate')}
                        name={`${controlId}-coordinate`}
                        onChange={handleChangeCoordinate}
                        options={booleanOptions}
                        value={coordinate}
                    />
                </SettingRow>

                {showMoveNumbers && (
                    <SettingRow
                        label={t('board_settings.move_numbers', {
                            defaultValue: 'Move numbers',
                        })}>
                        <SegmentedControl
                            ariaLabel={t('board_settings.move_numbers', {
                                defaultValue: 'Move numbers',
                            })}
                            name={`${controlId}-number`}
                            onChange={handleChangeNumber}
                            options={booleanOptions}
                            value={moveNumbers}
                        />
                    </SettingRow>
                )}

                {showBoardSize && (
                    <SettingRow
                        label={t('board_settings.board_size')}
                        labelFor={boardSizeId}>
                        <div className="flex items-center gap-3">
                            <input
                                className="h-8 w-24 rounded border border-neutral-300 bg-white px-2.5 text-sm text-[var(--ui-text)] outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 dark:border-neutral-600 dark:bg-neutral-900"
                                id={boardSizeId}
                                max={19}
                                min={3}
                                onChange={event =>
                                    handleChangeBoardSize(
                                        event.target.value
                                            ? Number.parseInt(
                                                  event.target.value,
                                                  10
                                              )
                                            : undefined
                                    )
                                }
                                type="number"
                                value={settings.boardSize ?? ''}
                            />
                            <span className="text-sm text-neutral-500 dark:text-neutral-400">
                                3–19
                            </span>
                        </div>
                    </SettingRow>
                )}

                {showResponseSpeed && (
                    <SettingRow label={t('rush_settings.response_speed')}>
                        <SegmentedControl
                            ariaLabel={t('rush_settings.response_speed')}
                            name={`${controlId}-response-speed`}
                            onChange={handleChangeResponseSpeed}
                            options={responseSpeedOptions}
                            value={responseSpeed}
                        />
                    </SettingRow>
                )}

                <SettingRow label={t('rush_settings.sound')}>
                    <SegmentedControl
                        ariaLabel={t('rush_settings.sound')}
                        name={`${controlId}-sound`}
                        onChange={handleChangeMoveSound}
                        options={booleanOptions}
                        value={moveSound}
                    />
                </SettingRow>

                <SettingRow
                    label={t('rush_settings.sound_volume')}
                    labelFor={soundVolumeId}>
                    <div className="flex max-w-2xl items-center gap-3">
                        <input
                            aria-valuetext={`${soundVolume}%`}
                            className="min-w-0 flex-1 cursor-pointer accent-blue-600 disabled:cursor-not-allowed disabled:opacity-45"
                            disabled={!moveSound}
                            id={soundVolumeId}
                            max={100}
                            min={0}
                            onChange={event =>
                                handleChangeSoundVolume(
                                    Number.parseInt(event.target.value, 10)
                                )
                            }
                            step={1}
                            type="range"
                            value={soundVolume}
                        />
                        <output
                            className="w-12 text-right text-sm tabular-nums text-neutral-600 dark:text-neutral-300"
                            htmlFor={soundVolumeId}>
                            {soundVolume}%
                        </output>
                    </div>
                </SettingRow>
            </div>

            {showClearBoard && onClearBoard && (
                <div className="flex justify-end pt-4">
                    <button
                        className="inline-flex min-h-8 items-center justify-center rounded bg-red-600 px-2.5 py-1 text-sm font-medium text-white transition-colors hover:bg-red-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 dark:bg-red-500 dark:hover:bg-red-600"
                        onClick={onClearBoard}
                        type="button">
                        {t('board_settings.clear_board')}
                    </button>
                </div>
            )}
        </div>
    );
};
